import { useShallow } from "zustand/shallow";
import { createApiClient, RefreshManager } from "@njbsoft/api-client";
import { useAppStore } from "@/store";
import { queryClient } from "@/lib/query-client";
import { clearAllDrafts } from "@/lib/test-entry/draft-store";
import { clearBulkEditWatches } from "@/components/bulk-edits/bulk-edit-watch";
import { readPersistedSessionTokens } from "@/lib/persisted-session";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Shared forced-logout side effect for the three session-end paths that never
 * touch the Sign Out button: RefreshManager.onRefreshFailed (expired/invalid
 * refresh token), the API client's onUnauthorized (a 401 with no
 * refreshManager, or one whose refresh attempt failed), and a sibling tab's
 * sign-out reaching `lib/session-sync`. Clears client auth
 * state, the recent-pages trail, and any staged offline test-entry draft
 * (answers + evidence blobs) BEFORE the hard navigation -- mirrors the Sign Out button's
 * clear-before-navigate ordering (`_authenticated.tsx`'s `handleSignOut`) so
 * a shared desk workstation never carries a staged draft or a stale session
 * past a forced session end. Not awaited by callers (RefreshManager /
 * openapi-fetch both invoke these synchronously); the draft clear is
 * best-effort and must never block the redirect on I/O failure.
 */
export async function forceLogout(): Promise<void> {
  useAppStore.getState().clearActiveOrg();
  useAppStore.getState().clearAuth();
  // Cleared here rather than left to the recorder's `isAuthenticated` effect:
  // the hard navigation below outruns a React effect, and the trail would sit
  // in this tab's session storage waiting for the next sign-in.
  useAppStore.getState().clearRecentPages();
  queryClient.clear();
  clearBulkEditWatches();
  try {
    await clearAllDrafts();
  } catch {
    // Silently ignore -- we are logging out regardless
  }
  window.location.href = "/login";
}

// Centralized refresh manager -- shared by API client interceptor and auth
// guard.
//
// The last three options are the cross-tab layer: the manager takes an
// exclusive Web Lock around every refresh and, inside it, adopts a sibling's
// rotation from the persisted store when that pair is stamped LATER than the
// one captured here — because a tab replaying a token its sibling rotated more
// than the server's grace window ago reads as token theft and revokes the whole
// rotation family.
export const refreshManager = new RefreshManager({
  baseUrl: API_URL,
  getRefreshToken: () => useAppStore.getState().refreshToken,
  onTokenRefreshed: (accessToken, refreshToken, expiresAt) => {
    const state = useAppStore.getState();
    state.setAuth(accessToken, state.user, refreshToken);
    state.setExpiresAt(new Date(expiresAt).getTime());
  },
  // Hard navigation is intentional: on auth failure a full page reload
  // provides a clean slate with no risk of stale state leaking. An
  // alternative (router.navigate) keeps SPA lifecycle but risks retaining
  // stale query cache or component state.
  onRefreshFailed: () => {
    void forceLogout();
  },
  getPersistedTokens: readPersistedSessionTokens,
  adoptTokens: (tokens) => {
    useAppStore.getState().adoptTokens(tokens);
  },
  getTokenStamp: () => useAppStore.getState().rotatedAt,
});

// Create API client with token getter and RefreshManager
export const apiClient = createApiClient({
  baseUrl: API_URL,
  getToken: () => useAppStore.getState().token,
  refreshManager,
  onUnauthorized: () => {
    void forceLogout(); // Hard nav intentional -- see onRefreshFailed comment
  },
});

// Hook for components
export function useApiClient() {
  return apiClient;
}

// Auth context for router
export function useAuth() {
  return useAppStore(
    useShallow((s) => ({
      token: s.token,
      refreshToken: s.refreshToken,
      expiresAt: s.expiresAt,
      user: s.user,
      isAuthenticated: s.isAuthenticated,
      setAuth: s.setAuth,
      setRefreshToken: s.setRefreshToken,
      setExpiresAt: s.setExpiresAt,
      rotatedAt: s.rotatedAt,
      adoptTokens: s.adoptTokens,
      setEmailVerified: s.setEmailVerified,
      clearAuth: s.clearAuth,
      mfaSessionToken: s.mfaSessionToken,
      mfaPending: s.mfaPending,
      mfaMethods: s.mfaMethods,
      setMfaChallenge: s.setMfaChallenge,
      clearMfaChallenge: s.clearMfaChallenge,
    })),
  );
}
